import React, { useState } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { TrendingUp, TrendingDown, Clock, DollarSign } from 'lucide-react';

const OrderPanel = ({ onTradeSubmit, currentPrice, selectedAsset }) => {
  const [tradeDirection, setTradeDirection] = useState('call');
  const { tt } = useLanguage();
  const [tradeAmount, setTradeAmount] = useState('25');
  const [tradeTime, setTradeTime] = useState(300); // секунды (5 минут по умолчанию)

  // Времена: 5м, 15м, 30м, 1ч, 4ч, 24ч, 30 дней
  const quickTimes = [
    300,      // 5 минут
    900,      // 15 минут
    1800,     // 30 минут
    3600,     // 1 час
    14400,    // 4 часа
    86400,    // 24 часа
    2592000   // 30 дней
  ];

  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}с`;
    if (seconds < 3600) {
      const minutes = Math.floor(seconds / 60);
      return `${minutes}м`;
    }
    if (seconds < 86400) {
      const hours = Math.floor(seconds / 3600);
      return `${hours}ч`;
    }
    const days = Math.floor(seconds / 86400);
    return `${days}д`;
  };

  const calculatePayout = () => {
    const amount = parseFloat(tradeAmount);
    if (!amount) return 0;
    return (amount * 0.85).toFixed(2); // 85% payout
  };

  const handleSubmit = () => {
    const amount = parseFloat(tradeAmount);
    if (!amount || amount < 5) {
      alert(tt('Минимальная сумма сделки: $5'));
      return;
    }

    const trade = {
      direction: tradeDirection,
      amount: amount,
      duration: tradeTime,
      strike_price: currentPrice,
      expected_payout: parseFloat(calculatePayout()),
      asset: selectedAsset
    };

    onTradeSubmit(trade);
  };

  return (
    <div className="bg-gray-800 rounded-lg p-4 space-y-4">
      {/* Trade Direction Buttons */}
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={() => setTradeDirection('call')}
          className={`flex items-center justify-center space-x-2 py-4 rounded-lg font-semibold text-lg transition-colors ${
            tradeDirection === 'call'
              ? 'bg-green-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          <TrendingUp className="w-6 h-6" />
          <span>{tt('КУПИТЬ')}</span>
        </button>
        
        <button
          onClick={() => setTradeDirection('put')}
          className={`flex items-center justify-center space-x-2 py-4 rounded-lg font-semibold text-lg transition-colors ${
            tradeDirection === 'put'
              ? 'bg-red-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          <TrendingDown className="w-6 h-6" />
          <span>{tt('ПРОДАТЬ')}</span>
        </button>
      </div>

      {/* Amount Input */}
      <div className="space-y-2">
        <label className="text-gray-400 text-sm flex items-center space-x-2">
          <DollarSign className="w-4 h-4" />
          <span>{tt('Сумма сделки')}</span>
        </label>
        <input
          type="text"
          inputMode="decimal"
          value={tradeAmount}
          onChange={(e) => {
            const value = e.target.value;
            // Разрешаем только цифры и точку, пустое поле тоже разрешаем
            if (value === '' || /^\d*\.?\d*$/.test(value)) {
              setTradeAmount(value);
            }
          }}
          className="w-full bg-gray-700 text-white px-3 py-3 rounded-lg focus:ring-2 focus:ring-blue-500 text-center text-xl font-bold"
          placeholder="25"
        />
      </div>

      {/* Time Expiration */}
      <div className="space-y-2">
        <label className="text-gray-400 text-sm flex items-center space-x-2">
          <Clock className="w-4 h-4" />
          <span>{tt('Время экспирации')}</span>
        </label>
        <select
          value={tradeTime}
          onChange={(e) => setTradeTime(parseInt(e.target.value))}
          className="w-full bg-gray-700 text-white px-3 py-3 rounded-lg focus:ring-2 focus:ring-blue-500 text-center"
        >
          {quickTimes.map((time) => (
            <option key={time} value={time} className="bg-gray-700">
              {formatTime(time)}
            </option>
          ))}
        </select>
      </div>

      {/* Payout Display */}
      <div className="bg-gray-700 rounded-lg p-3">
        <div className="flex justify-between text-sm text-gray-400">
          <span>{tt('Потенциальная прибыль:')}</span>
          <span className="text-green-400 font-bold">${calculatePayout()}</span>
        </div>
      </div>

      {/* Submit Button */}
      <button
        onClick={handleSubmit}
        className={`w-full py-4 rounded-lg font-bold text-lg transition-colors ${
          tradeDirection === 'call'
            ? 'bg-green-600 hover:bg-green-700 text-white'
            : 'bg-red-600 hover:bg-red-700 text-white'
        }`}
      >
        {tradeDirection === 'call' ? 'КУПИТЬ' : 'ПРОДАТЬ'} за ${tradeAmount || '0'}
      </button>
    </div>
  );
};

export default OrderPanel;